import type { Direction } from '../sim/direction';

export interface ContinuousObservedPose {
  x: number;
  y: number;
  z: number;
  facing: Direction;
  speedTilesPerSecond: number;
}

export type ContinuousPositionConstraint = (
  start: { x: number; y: number },
  displacement: { x: number; y: number },
  level: number,
) => { x: number; y: number };

const TELEPORT_DISTANCE_TILES = 1.5;
const POSITION_EPSILON = 1e-6;

interface Hurl {
  from: { x: number; y: number };
  to: { x: number; y: number };
  durationMs: number;
  elapsedMs: number;
}

/**
 * Smoothly follows fractional authoritative positions without predicting.
 *
 * Observations form a short authoritative polyline. The follower consumes each
 * point at the declared server gait, staying about one snapshot behind without
 * inventing enemy intent or cutting across a server-safe corner.
 */
export class ContinuousPositionAnimator {
  private point: { x: number; y: number };
  private latest: ContinuousObservedPose;
  private readonly targets: ContinuousObservedPose[] = [];
  private level: number;
  private drawFacing: Direction;
  private movedThisFrame = false;
  private hurling?: Hurl;

  constructor(
    initial: ContinuousObservedPose,
    private readonly constrain?: ContinuousPositionConstraint,
  ) {
    this.point = { x: initial.x, y: initial.y };
    this.latest = { ...initial };
    this.level = initial.z;
    this.drawFacing = initial.facing;
  }

  get z(): number {
    return this.level;
  }

  get facing(): Direction {
    return this.drawFacing;
  }

  get isMoving(): boolean {
    return (
      this.movedThisFrame ||
      this.hurling !== undefined ||
      this.targets.length > 0
    );
  }

  observe(pose: ContinuousObservedPose): void {
    this.drawFacing = pose.facing;
    if (
      pose.z !== this.level ||
      Math.hypot(pose.x - this.latest.x, pose.y - this.latest.y) >
        TELEPORT_DISTANCE_TILES
    ) {
      this.point = { x: pose.x, y: pose.y };
      this.level = pose.z;
      this.hurling = undefined;
      this.targets.length = 0;
    } else if (
      Math.hypot(pose.x - this.latest.x, pose.y - this.latest.y) >
      POSITION_EPSILON
    ) {
      this.targets.push({ ...pose });
    } else if (this.targets.length > 0) {
      this.targets[this.targets.length - 1] = { ...pose };
    }
    this.latest = { ...pose };
  }

  update(
    elapsedMs: number,
    constrain = this.constrain,
  ): void {
    this.movedThisFrame = false;
    if (elapsedMs <= 0 || !Number.isFinite(elapsedMs)) return;
    if (this.hurling) {
      const hurl = this.hurling;
      hurl.elapsedMs = Math.min(
        hurl.durationMs,
        hurl.elapsedMs + elapsedMs,
      );
      const progress = hurl.elapsedMs / hurl.durationMs;
      const desired = {
        x: hurl.from.x + (hurl.to.x - hurl.from.x) * progress,
        y: hurl.from.y + (hurl.to.y - hurl.from.y) * progress,
      };
      const next = constrain
        ? constrain(
            this.point,
            {
              x: desired.x - this.point.x,
              y: desired.y - this.point.y,
            },
            this.level,
          )
        : desired;
      this.movedThisFrame =
        Math.hypot(next.x - this.point.x, next.y - this.point.y) >
        POSITION_EPSILON;
      this.point = next;
      if (progress >= 1) {
        this.hurling = undefined;
        if (
          Math.hypot(
            hurl.to.x - this.point.x,
            hurl.to.y - this.point.y,
          ) > POSITION_EPSILON
        ) {
          this.targets.unshift({
            ...this.latest,
            x: hurl.to.x,
            y: hurl.to.y,
            z: this.level,
          });
        }
      }
      return;
    }

    let remainingMs = elapsedMs;
    while (remainingMs > 0 && this.targets.length > 0) {
      const target = this.targets[0]!;
      const dx = target.x - this.point.x;
      const dy = target.y - this.point.y;
      const distance = Math.hypot(dx, dy);
      if (distance <= POSITION_EPSILON) {
        this.point = { x: target.x, y: target.y };
        this.targets.shift();
        continue;
      }
      const speed = Math.max(0, target.speedTilesPerSecond);
      if (speed <= 0) return;
      const availableTravel = speed * remainingMs / 1000;
      const travel = Math.min(distance, availableTravel);
      const desired = {
        x: this.point.x + dx / distance * travel,
        y: this.point.y + dy / distance * travel,
      };
      const next = constrain
        ? constrain(
            this.point,
            {
              x: desired.x - this.point.x,
              y: desired.y - this.point.y,
            },
            target.z,
          )
        : desired;
      const actualTravel = Math.hypot(
        next.x - this.point.x,
        next.y - this.point.y,
      );
      this.point = next;
      if (actualTravel <= POSITION_EPSILON) return;
      this.movedThisFrame = true;
      if (
        actualTravel + POSITION_EPSILON >= distance &&
        Math.hypot(target.x - this.point.x, target.y - this.point.y) <=
          POSITION_EPSILON
      ) {
        this.point = { x: target.x, y: target.y };
        this.targets.shift();
        remainingMs -= distance / speed * 1000;
      } else {
        remainingMs = 0;
      }
    }
  }

  hurl(
    from: { x: number; y: number },
    to: { x: number; y: number },
    durationMs: number,
  ): void {
    if (from.x === to.x && from.y === to.y) return;
    this.point = { ...from };
    this.latest = { ...this.latest, ...to };
    this.targets.length = 0;
    this.hurling = {
      from: { ...from },
      to: { ...to },
      durationMs: Math.max(1, durationMs),
      elapsedMs: 0,
    };
  }

  renderPosition(): { x: number; y: number } {
    return { ...this.point };
  }
}
